import React from 'react';

/**
 * Indicator dashboard primitives.
 *  - <SharedHeader/>     : NIFTY price + VIX strip + "global" indicators (same for all TFs).
 *  - <TimeframePanel/>   : per-timeframe panel with bias, buy setup, and TF-specific indicators.
 *  - default export      : legacy full dashboard (kept for historical replay).
 */

const SIGNAL_META = {
  BULLISH:    { label: 'Bullish',    cls: 'bull',  icon: '▲' },
  BEARISH:    { label: 'Bearish',    cls: 'bear',  icon: '▼' },
  NEUTRAL:    { label: 'Range',      cls: 'neut',  icon: '●' },
  NO_TRADE:   { label: 'No Trade',   cls: 'warn',  icon: '⛔' },
  EXIT_TRADE: { label: 'Exit',       cls: 'warn',  icon: '⚠' },
  OK:         { label: 'OK',         cls: 'ok',    icon: '✓' },
  WAITING:    { label: 'Waiting',    cls: 'wait',  icon: '⏳' },
};

function Pill({ signal }) {
  const m = SIGNAL_META[signal] || SIGNAL_META.WAITING;
  return <span className={`ind-pill ind-pill-${m.cls}`}>{m.icon} {m.label}</span>;
}

function IndCard({ icon, name, signal, value, sub, accent }) {
  const m = SIGNAL_META[signal] || SIGNAL_META.WAITING;
  return (
    <div className={`ind-card ind-acc-${accent || m.cls}`}>
      <div className="ind-card-head">
        <span className="ind-card-icon">{icon}</span>
        <span className="ind-card-name">{name}</span>
        <Pill signal={signal} />
      </div>
      <div className="ind-card-value">{value ?? '—'}</div>
      {sub && <div className="ind-card-sub">{sub}</div>}
    </div>
  );
}

function fmt(n, dec = 2) {
  if (n === undefined || n === null || n === 0) return '—';
  return Number(n).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtInt(n) { return n ? Math.round(n).toLocaleString('en-IN') : '—'; }
function fmtPct(n, dec = 2) { return (n === undefined || n === null) ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(dec)}%`; }

function det(data, key) {
  const list = data?.indicators || [];
  return list.find(i => i.name === key)?.value || '';
}

// ─────────────────────────────────────────────────────────────────────────
// Shared header — NIFTY price card + VIX strip + global-scope indicator row
// (these come from daily / pre-market data so they're identical across TFs)
// ─────────────────────────────────────────────────────────────────────────
export function SharedHeader({ data }) {
  if (!data) return null;
  const niftyChange = (data.nifty_price || 0) - (data.nifty_prev_close || 0);
  const niftyChangePct = data.nifty_prev_close > 0 ? (niftyChange / data.nifty_prev_close) * 100 : 0;

  return (
    <>
      {/* Price hero + benchmark strip */}
      <div className="ind-shared-top">
        <div className="ind-hero-price ind-hero-price-wide">
          <div className="ind-hp-label">NIFTY 50</div>
          <div className="ind-hp-value">{fmt(data.nifty_price, 2)}</div>
          <div className={`ind-hp-change ${niftyChange >= 0 ? 'pos' : 'neg'}`}>
            {niftyChange >= 0 ? '▲' : '▼'} {Math.abs(niftyChange).toFixed(0)} pts ({fmtPct(niftyChangePct)})
          </div>
          <div className="ind-hp-meta">
            <span>O <strong>{fmtInt(data.nifty_open)}</strong></span>
            <span>H <strong>{fmtInt(data.nifty_high)}</strong></span>
            <span>L <strong>{fmtInt(data.nifty_low)}</strong></span>
            <span>PC <strong>{fmtInt(data.nifty_prev_close)}</strong></span>
          </div>
        </div>

        <div className="ind-vix-stack">
          <div className="ind-vix-block">
            <span className="ind-vix-label">INDIA VIX</span>
            <span className="ind-vix-value">{fmt(data.vix_value, 2)}</span>
            <span className={`ind-vix-chg ${(data.vix_change_pct || 0) >= 0 ? 'pos' : 'neg'}`}>
              {fmtPct(data.vix_change_pct)}
            </span>
            <Pill signal={data.vix_signal} />
          </div>
          <div className="ind-vix-block">
            <span className="ind-vix-label">SENSEX</span>
            <span className="ind-vix-value">{fmt(data.sensex_price, 2)}</span>
          </div>
          <div className="ind-vix-block">
            <span className="ind-vix-label">BANKNIFTY</span>
            <span className="ind-vix-value">{fmt(data.banknifty_price, 2)}</span>
          </div>
        </div>
      </div>

      {/* Shared (global) indicator row */}
      <h3 className="ind-group-title">🌐 Shared Market Context</h3>
      <div className="ind-grid">
        <IndCard
          icon="📊" name="Daily 20 DMA" signal={data.daily_20dma_signal}
          value={fmt(data.daily_20dma)}
          sub={det(data, 'Nifty 20 DMA (Daily)')}
        />
        <IndCard
          icon="📅" name="Previous Day H/L" signal={data.prev_day_signal}
          value={`${fmtInt(data.prev_day_high)} / ${fmtInt(data.prev_day_low)}`}
          sub={det(data, 'Previous Day High/Low')}
        />
        <IndCard
          icon="🕘" name="First 15-Min Candle" signal={data.first_15m_signal}
          value={`${fmtInt(data.first_15m_high)} / ${fmtInt(data.first_15m_low)}`}
          sub={det(data, 'First 15-Min Candle')}
        />
        <IndCard
          icon="⬆️⬇️" name="Opening Gap" signal={data.gap_signal}
          value={`${(data.gap_points || 0) > 0 ? '+' : ''}${fmtInt(data.gap_points)} pts`}
          sub={det(data, 'Gap Up/Down') || 'No-trade if |gap| ≥ 300 pts'}
        />
        <IndCard
          icon="🚀" name="Intraday Rally" signal={data.intraday_signal}
          value={`${fmtInt(data.intraday_range)} pts`}
          sub={det(data, 'Intraday Rally') || 'Exit if range ≥ 300 pts'}
        />
        <IndCard
          icon="📏" name="Day Range" signal="NEUTRAL"
          value={`${fmtInt(data.nifty_high)} – ${fmtInt(data.nifty_low)}`}
          sub={`Spread: ${fmtInt(data.intraday_range)} pts`}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TimeframePanel — bias hero + per-TF indicator grid
// ─────────────────────────────────────────────────────────────────────────
export function TimeframePanel({ data, tfLabel }) {
  if (!data) {
    return (
      <div className="ind-tf-panel">
        <div className="ind-tf-header">
          <span className="ind-tf-badge">{tfLabel}</span>
          <span className="ind-tf-status">Waiting for data…</span>
        </div>
      </div>
    );
  }

  const buySignal = data.intraday_buy_signal || 'WAITING';
  const overall = data.overall_bias || 'WAITING';
  const overallMeta = SIGNAL_META[overall] || SIGNAL_META.WAITING;
  const buyDetail = det(data, `Intraday Buy Setup (${data.timeframe})`)
                 || det(data, 'Intraday Buy Setup (5m)');

  return (
    <div className="ind-tf-panel">
      <div className="ind-tf-header">
        <span className="ind-tf-badge">{tfLabel}</span>
        <span className="ind-tf-status">Per-timeframe indicators</span>
      </div>

      {/* Bias + Buy setup combined hero card */}
      <div className={`ind-tf-hero bias-${overallMeta.cls}`}>
        <div className="ind-tf-hero-row">
          <div className="ind-tf-bias">
            <span className="ind-hb-label">Bias</span>
            <span className="ind-tf-bias-value">{overallMeta.icon} {overallMeta.label}</span>
            <span className="ind-hb-tally">
              <span className="bull-tally">▲ {data.bullish_count || 0}</span>
              <span className="sep">·</span>
              <span className="bear-tally">▼ {data.bearish_count || 0}</span>
            </span>
          </div>
          <div className={`ind-tf-buy buy-${buySignal === 'BULLISH' ? 'on' : buySignal === 'WAITING' ? 'wait' : 'off'}`}>
            <span className="ind-hb-label">Buy Setup</span>
            <span className="ind-tf-buy-value">
              {buySignal === 'BULLISH' ? '🟢 BUY READY' : buySignal === 'WAITING' ? '⏳ WAITING' : '⚪ NO BUY'}
            </span>
            <span className={`ind-hb-trade ${data.trade_allowed ? 'allowed' : 'blocked'}`}>
              {data.trade_allowed ? '✓ Trade Allowed' : '✕ No Trade'}
            </span>
          </div>
        </div>
        {buyDetail && <div className="ind-tf-hero-sub">{buyDetail}</div>}
      </div>

      <div className="ind-tf-grid">
        <IndCard
          icon="📉" name="5m / 200 DMA" signal={data.fivemin_dma_signal}
          value={`${fmtInt(data.fivemin_20dma)} / ${fmtInt(data.fivemin_200dma)}`}
          sub={det(data, 'Nifty 20 & 200 DMA (5m)')}
        />
        <IndCard
          icon="🛡️" name="Supertrend" signal={data.supertrend_signal}
          value={fmt(data.supertrend)}
          sub={det(data, 'Supertrend (10,3)')}
        />
        <IndCard
          icon="💪" name="ADX (14)" signal={data.adx_signal}
          value={fmt(data.adx)}
          sub={det(data, 'ADX (14)') || 'Trend strength'}
        />
        <IndCard
          icon="🌀" name="RSI (14)" signal={data.rsi_signal}
          value={fmt(data.rsi)}
          sub={det(data, 'RSI (14)') || 'Buy 25-30 · Sell 70-75'}
        />
        <IndCard
          icon="📐" name="MACD" signal={data.macd_signal}
          value={fmt(data.macd, 4)}
          sub={det(data, 'MACD (12,26)') || `Prev ${fmt(data.macd_prev, 4)}`}
        />
        <IndCard
          icon="📍" name="VWAP" signal={data.vwap_signal}
          value={fmt(data.vwap)}
          sub={det(data, 'VWAP')}
        />
        <IndCard
          icon="📡" name="Keltner" signal={data.keltner_signal}
          value={fmt(data.keltner_middle)}
          sub={`U ${fmtInt(data.keltner_upper)} · L ${fmtInt(data.keltner_lower)}`}
        />
        <IndCard
          icon="🌪️" name="ATR (14)" signal={data.atr_signal}
          value={fmt(data.atr)}
          sub={det(data, 'ATR (14)') || `${fmt(data.atr_pct, 2)}% of spot`}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Default — full single-TF dashboard (kept for backward compat callers)
// ─────────────────────────────────────────────────────────────────────────
export default function IndicatorDashboard({ data, timeLabel }) {
  return (
    <div className="ind-dash">
      {timeLabel && (
        <div className="ind-time-banner">Snapshot @ <strong>{timeLabel}</strong></div>
      )}
      <SharedHeader data={data} />
      <TimeframePanel data={data} tfLabel={(data?.timeframe || '5m').toUpperCase()} />
    </div>
  );
}
